import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Loader2, Save, Building2, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import ImageUpload from "../../components/admin/ImageUpload";
import Pagination from "../../components/admin/Pagination";

const empty = { name: "", logo_url: "", website_url: "", order: 0, is_active: true };

const AdminClients = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  useEffect(() => { setPage(1); }, [pageSize]);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const paged = items.slice((page - 1) * pageSize, page * pageSize);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/clients");
      setItems(res.data);
    } catch { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId("new"); setForm({ ...empty, order: items.length }); };
  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({ name: item.name, logo_url: item.logo_url || "", website_url: item.website_url || "", order: item.order || 0, is_active: item.is_active !== false });
  };
  const close = () => { setEditingId(null); setForm(empty); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    setSaving(true);
    try {
      if (editingId === "new") { await api.post("/admin/clients", form); toast.success("Client added"); }
      else { await api.put(`/admin/clients/${editingId}`, form); toast.success("Client updated"); }
      await load(); close();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this client?")) return;
    try {
      await api.delete(`/admin/clients/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const toggle = async (item) => {
    try {
      await api.put(`/admin/clients/${item.id}`, { ...item, is_active: !item.is_active });
      setItems((p) => p.map((c) => c.id === item.id ? { ...c, is_active: !c.is_active } : c));
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="admin-clients-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Clients</h1>
          <p className="mt-1 text-sm text-slate-500">Manage client logos displayed on the website.</p>
        </div>
        <button onClick={openNew} className="admin-btn-primary" data-testid="clients-add-btn">
          <Plus size={16} /> Add Client
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <div className="inline-flex w-14 h-14 bg-slate-100 text-slate-400 rounded-full items-center justify-center mb-4"><Building2 size={22} /></div>
          <h3 className="font-semibold text-slate-900">No clients yet</h3>
          <p className="mt-1 text-sm text-slate-500">Add your first client to showcase on the website.</p>
        </div>
      ) : (
        <>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {paged.map((item) => (
              <li key={item.id} className="px-5 py-4 flex items-center gap-4" data-testid={`client-row-${item.id}`}>
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center shrink-0 overflow-hidden border border-slate-200">
                  {item.logo_url ? (
                    <img src={item.logo_url} alt={item.name} className="w-full h-full object-contain p-1" />
                  ) : (
                    <Building2 size={20} className="text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{item.name}</div>
                  {item.website_url && (
                    <a href={item.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-orange-600 flex items-center gap-1 mt-0.5 truncate">
                      <ExternalLink size={10} /> {item.website_url}
                    </a>
                  )}
                </div>
                <button onClick={() => toggle(item)} className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${item.is_active ? "border-green-200 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                  {item.is_active ? "Active" : "Hidden"}
                </button>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => openEdit(item)} className="admin-btn-ghost p-2" data-testid={`client-edit-${item.id}`}><Edit2 size={14} /></button>
                  <button onClick={() => remove(item.id)} className="admin-btn-ghost p-2 text-red-500 hover:text-red-700" data-testid={`client-delete-${item.id}`}><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={items.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[8, 12, 16, 24]}
        />
        </>
      )}

      {editingId && (
        <div className="fixed inset-0 z-50 admin-modal-backdrop flex items-center justify-center p-3 md:p-6" onClick={close}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] shadow-2xl admin-fade-up flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3.5 border-b border-slate-200 flex justify-between items-center shrink-0">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600">{editingId === "new" ? "New" : "Edit"}</p>
                <h3 className="text-lg font-bold text-slate-900">Client</h3>
              </div>
              <button onClick={close} className="admin-btn-ghost p-2"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto admin-scroll flex-1">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Client Name <span className="text-red-500">*</span></label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="admin-input" placeholder="e.g. Qatar Building Company" data-testid="client-form-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Logo</label>
                <ImageUpload value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} testId="client-form-logo" aspectClass="w-28 h-20" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Website URL <span className="text-slate-400 font-normal">(optional)</span></label>
                <input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} className="admin-input" placeholder="https://..." data-testid="client-form-website" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Display Order</label>
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="admin-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Visibility</label>
                  <select value={form.is_active ? "1" : "0"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })} className="admin-input">
                    <option value="1">Active (visible)</option>
                    <option value="0">Hidden</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex gap-2 shrink-0">
              <button onClick={close} className="admin-btn-secondary flex-1">Cancel</button>
              <button onClick={save} disabled={saving} className="admin-btn-primary flex-1" data-testid="client-form-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClients;
